import { useCallback, useRef, useState } from "react"
import {
  Modal,
  Platform,
  Pressable,
  TextStyle,
  TouchableOpacity,
  View,
  ViewStyle,
} from "react-native"

import MaterialIcons from "@expo/vector-icons/MaterialIcons"
import { router, useFocusEffect } from "expo-router"
import * as ScreenOrientation from "expo-screen-orientation"
import { StatusBar } from "expo-status-bar"
import { useSafeAreaInsets } from "react-native-safe-area-context"

import type { ThemePreference } from "src/hooks/useAppTheme"
import { CloseButton } from "src/components/CloseButton"
import { Button, Text } from "src/components/Ignite"
import { SidekickView } from "src/components/SidekickView"
import { useAuth } from "src/context/AuthContext"
import { useAppTheme } from "src/hooks/useAppTheme"
import { useSponsorList } from "src/queries/useUser"
import { ThemedStyle } from "src/theme"
import { backOrToRoot } from "src/utils/routerHelpers"

// ─── Theme Selector ─────────────────────────────────────────────────────────

type ThemeOption = { label: string; value: ThemePreference }

const THEME_OPTIONS: ThemeOption[] = [
  { label: "System Default", value: "system" },
  { label: "Dark", value: "dark" },
  { label: "Light", value: "light" },
]

function ThemeSelect() {
  const { theme, themePreference, setThemeContextOverride } = useAppTheme()
  const [open, setOpen] = useState(false)
  const buttonRef = useRef<View>(null)
  const [buttonLayout, setButtonLayout] = useState<{
    x: number
    y: number
    width: number
    height: number
  } | null>(null)

  const selectedLabel =
    THEME_OPTIONS.find((o) => o.value === themePreference)?.label ?? "System Default"

  const handleButtonPress = () => {
    buttonRef.current?.measureInWindow((x, y, width, height) => {
      setButtonLayout({ x, y, width, height })
      setOpen(true)
    })
  }

  const handleSelect = (value: ThemePreference) => {
    setThemeContextOverride(value === "system" ? undefined : value)
    setOpen(false)
  }

  return (
    <View style={$settingGroup}>
      <Text style={[$settingLabel, { color: theme.colors.textDim }]}>Theme</Text>
      <View ref={buttonRef} collapsable={false}>
        <TouchableOpacity
          style={[$selectButton, { backgroundColor: theme.colors.buttonBackground }]}
          onPress={handleButtonPress}
          accessibilityRole="button"
          accessibilityLabel={`Theme: ${selectedLabel}`}
        >
          <Text style={[$selectButtonText, { color: theme.colors.buttonText }]}>
            {selectedLabel}
          </Text>
          <MaterialIcons
            name={open ? "keyboard-arrow-up" : "keyboard-arrow-down"}
            size={20}
            color={theme.colors.buttonText}
          />
        </TouchableOpacity>
      </View>

      <Modal visible={open} transparent animationType="none" onRequestClose={() => setOpen(false)}>
        <Pressable style={$modalBackdrop} onPress={() => setOpen(false)}>
          {buttonLayout && (
            <View
              style={[
                $dropdownMenu,
                {
                  backgroundColor: theme.colors.buttonBackground,
                  top: buttonLayout.y + buttonLayout.height + 4,
                  left: buttonLayout.x,
                  width: buttonLayout.width,
                },
              ]}
            >
              {THEME_OPTIONS.map((option) => (
                <TouchableOpacity
                  key={option.value}
                  style={$dropdownOption}
                  onPress={() => handleSelect(option.value)}
                  accessibilityRole="menuitem"
                >
                  <MaterialIcons
                    name="check"
                    size={16}
                    color={
                      option.value === themePreference ? theme.colors.buttonText : "transparent"
                    }
                    style={{ marginRight: 8 }}
                  />
                  <Text style={[$dropdownOptionText, { color: theme.colors.buttonText }]}>
                    {option.label}
                  </Text>
                </TouchableOpacity>
              ))}
            </View>
          )}
        </Pressable>
      </Modal>
    </View>
  )
}

export default function SettingsScreen() {
  const insets = useSafeAreaInsets()
  const { themed, navTheme } = useAppTheme()
  const { getUserEmail } = useAuth()
  const sponsorList = useSponsorList()

  useFocusEffect(
    useCallback(() => {
      ScreenOrientation.lockAsync(ScreenOrientation.OrientationLock.PORTRAIT_UP).catch(() => {})
    }, []),
  )

  const handleClose = useCallback(() => {
    backOrToRoot()
  }, [])

  return (
    <View style={themed($container)}>
      <StatusBar style={navTheme.dark ? "light" : "dark"} />

      <SidekickView
        showAvatar
        autoShowAvatarOnSpeech
        showCaptions
        showCCToggle
        showButtonCluster
        compactButtonCluster
        darkBackground
        style={themed($container)}
        onMenuPress={handleClose}
        riveOwner="video"
        controlsConfig={{ type: "none" }}
      >
        {/* Header */}
        <View
          style={[
            themed($headerContainer),
            { top: insets.top, left: insets.left, right: insets.right },
          ]}
          pointerEvents="box-none"
        >
          <Text style={themed($headerTitle)}>Settings</Text>
          <CloseButton
            onPress={handleClose}
            accessibilityLabel="Close settings"
            style={{ position: "absolute", top: 22, right: 16 }}
          />
        </View>

        {/* Settings Options */}
        <View
          style={[
            themed($content),
            {
              paddingTop: insets.top + 72, // header height + padding
              paddingBottom: 120, // space for ButtonCluster
            },
          ]}
        >
          <View style={{ height: 24 }} />
          <ThemeSelect />
          <View style={{ height: 24 }} />
          <Button
            text="Select Your Personal Sidekick"
            onPress={() => router.replace("/select-msk" as any)}
            preset="subtle"
          />
          {sponsorList.length > 1 && (
            <Button
              text="Change Channel"
              onPress={() => router.replace("/change-channel" as any)}
              preset="subtle"
            />
          )}
          <Button
            text="Change Password"
            onPress={() =>
              router.replace({
                pathname: "/unauth/update-password" as any,
                params: { email: getUserEmail() || "" },
              })
            }
            preset="subtle"
          />
        </View>
      </SidekickView>
    </View>
  )
}

const $container: ThemedStyle<ViewStyle> = ({ colors }) => ({
  flex: 1,
  backgroundColor: colors.background,
})

const $headerContainer: ThemedStyle<ViewStyle> = () => ({
  position: Platform.OS === "web" ? ("fixed" as any) : "absolute",
  zIndex: 2100,
  flexDirection: "row",
  justifyContent: "center",
  alignItems: "center",
  paddingHorizontal: 16,
  height: 80,
  // No backgroundColor — see menu.tsx for rationale (avoid occluding the
  // tab-mode avatar that lives in MediaPlayerShell's foreground layer).
})

const $headerTitle: ThemedStyle<TextStyle> = ({ colors }) => ({
  fontSize: 18,
  fontWeight: "700",
  color: colors.text,
})

const $content: ThemedStyle<ViewStyle> = () => ({
  flex: 1,
  paddingHorizontal: 24,
  gap: 16,
})

const $settingGroup: ViewStyle = {
  gap: 6,
}

const $settingLabel: TextStyle = {
  fontSize: 13,
  fontWeight: "500",
  letterSpacing: 0.5,
}

const $selectButton: ViewStyle = {
  flexDirection: "row",
  alignItems: "center",
  justifyContent: "space-between",
  borderRadius: 10,
  paddingHorizontal: 14,
  paddingVertical: 12,
}

const $selectButtonText: TextStyle = {
  fontSize: 15,
}

const $modalBackdrop: ViewStyle = {
  flex: 1,
}

const $dropdownMenu: ViewStyle = {
  position: "absolute",
  borderRadius: 10,
  overflow: "hidden",
  shadowColor: "#000",
  shadowOffset: { width: 0, height: 4 },
  shadowOpacity: 0.3,
  shadowRadius: 8,
  elevation: 8,
}

const $dropdownOption: ViewStyle = {
  flexDirection: "row",
  alignItems: "center",
  paddingHorizontal: 14,
  paddingVertical: 12,
}

const $dropdownOptionText: TextStyle = {
  fontSize: 15,
}
